import { Observable, of } from 'rxjs';

export interface CategoryElement {
  id: string;
  name: string;
  system?: string;
  _resource?: any;
}

export const categoriesAdapter = {
  /**
   * transforms fhir4.coding to CategoryElement
   * @param coding that should be transformed
   * @returns
   */
  fromResource(coding: fhir4.Coding): CategoryElement {
    const categoryElement: CategoryElement = {
      id: coding.code,
      name: coding.display,
    };

    if (coding.system) categoryElement.system = coding.system;

    return categoryElement;
  },

  fromResource$(coding: fhir4.Coding): Observable<CategoryElement> {
    const categoryElement: CategoryElement = {
      id: coding.code,
      name: coding.display,
    };

    if (coding.system) categoryElement.system = coding.system;

    return of(categoryElement);
  },

  /**
   * transforms CategoryElement to fhir4.coding
   * @param categoryElement that should be transformed
   * @returns
   */
  toResource(categoryElement: CategoryElement): fhir4.Coding {
    const coding: fhir4.Coding = {
      code: categoryElement.id,
      display: categoryElement.name,
    };

    if (categoryElement.system) {
      coding.system = categoryElement.system;
    }

    return coding;
  },

  /**
   * get categories from resource (fhir4.CodeableConcept or array of fhir4.CodeableConcept)
   * @param resource
   * @returns
   */
  getCategories(
    resource: fhir4.CodeableConcept | fhir4.CodeableConcept[] = [],
  ): CategoryElement[] {
    const list: fhir4.CodeableConcept[] = Array.isArray(resource)
      ? resource
      : [resource];

    return list.reduce((acc, codeableConcept: fhir4.CodeableConcept) => {
      const codings: fhir4.Coding[] = codeableConcept?.coding || [];
      return [...acc, ...codings.map((coding) => this.fromResource(coding))];
    }, []);
  },

  /**
   * checks if resource (fhir4.CodeableConcept or array of fhir4.CodeableConcept) has category
   * @param resource
   * @param categoryElement
   */
  hasCategory(
    resource: fhir4.CodeableConcept | fhir4.CodeableConcept[] = [],
    categoryElement: CategoryElement,
  ): boolean {
    return this.getCategories(resource).some(
      ({ id }) => id === categoryElement.id,
    );
  },

  /**
   * adds/inserts category as coding to passed CodeableConcept
   * @param resource fhir4.CodeableConcept where category should be added as coding
   * @param categoryElement category that should be added/inserted
   */
  addCoding(
    resource: fhir4.CodeableConcept = {},
    categoryElement: CategoryElement,
  ): void {
    const coding: fhir4.Coding = this.toResource(categoryElement);
    resource.coding = [...(resource.coding || []), coding];
  },

  /**
   * adds/inserts category to passed ressource (fhir4.CodeableConcept or array of fhir4.CodeableConcept)
   * @param resource where category should be added/inserted
   * @param categoryElement category that should be added/inserted
   */
  addCategory(
    resource: fhir4.CodeableConcept | fhir4.CodeableConcept[] = [],
    categoryElement: CategoryElement,
  ): void {
    if (!resource || this.hasCategory(resource, categoryElement)) return;
    if (Array.isArray(resource)) {
      const codeableConcept: fhir4.CodeableConcept = {};
      this.addCoding(codeableConcept, categoryElement);
      resource.push(codeableConcept);
    } else {
      this.addCoding(resource, categoryElement);
    }
  },

  /**
   * removes category as coding from passed CodeableConcept
   * @param resource fhir4.CodeableConcept where category as coding should be removed
   * @param categoryElement category that should be removed (if exists)
   */
  removeCoding(
    resource: fhir4.CodeableConcept,
    categoryElement: CategoryElement,
  ): void {
    if (!resource?.coding?.length) return;

    resource.coding = resource.coding.filter(
      ({ code }) => code !== categoryElement.id,
    );
  },

  /**
   * removes category from passed ressource (fhir4.CodeableConcept or array of fhir4.CodeableConcept)
   * @param resource where category should be removed
   * @param categoryElement category that should be removed
   */
  removeCategory(
    resource: fhir4.CodeableConcept | fhir4.CodeableConcept[] = [],
    categoryElement: CategoryElement,
  ): void {
    if (!resource || !this.hasCategory(resource, categoryElement)) return;
    if (Array.isArray(resource)) {
      // remove coding from all codeable concepts
      resource.forEach((codeableConcept) =>
        this.removeCoding(codeableConcept, categoryElement),
      );
      // if codeableConcept has empty coding array, it should be removed
      while (resource.find(({ coding }) => !coding.length)) {
        const index = resource.findIndex(({ coding }) => !coding.length);
        resource.splice(index, 1);
      }
    } else {
      this.removeCoding(resource, categoryElement);
      // if codeableConcept has empty coding array, it should be cleared
      if (!resource.coding.length) {
        delete resource.coding;
        delete resource.text;
      }
    }
  },
};
